import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileSpreadsheet, FileText, Info, PiggyBank, Trash2 } from "lucide-react";
import { useAuth } from "@/context/use-auth";
import { useSocialAccountTranslation } from "@/i18n/hooks";
import { Heading } from "@/components/tailwind/heading";
import { Text } from "@/components/tailwind/text";
import { Button } from "@/components/tailwind/button";
import { Card } from "@/components/ui/card";
import { Input, InputGroup } from "@/components/tailwind/input";
import { Checkbox } from "@/components/tailwind/checkbox";
import { Field, Label } from "@/components/tailwind/fieldset";
import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/tailwind/table";
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from "@/components/tailwind/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { formatDateTimeString } from "@/lib/utils";
import {
    downloadSocialAccountStatementExcel,
    downloadSocialAccountStatementPdf,
    getSocialAccountStatement,
    reverseVoluntaryDonation,
    SocialAccountStatement,
    SocialAccountStatementEntry,
    StatementDateRange
} from "@/services/social-account-services";
import { DateRange, PeriodStepperFilter } from "@/components/date-range/period-stepper-filter";
import { ProblemDetail } from "@/types/problem-details";
import log from '@/lib/logger'

const SEARCH_DEBOUNCE_MS = 300;

function triggerDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

function ReferenceCell({ entry }: { entry: SocialAccountStatementEntry }) {
    const { t } = useSocialAccountTranslation();
    const hasReference = entry.orderId !== null || entry.memberId !== null;

    if (!hasReference) {
        return <span className="text-zinc-400">–</span>;
    }

    return (
        <div className="flex flex-col gap-1">
            {entry.orderId && (
                <Link
                    to={`/orders/${entry.orderId}`}
                    className="text-blue-600 hover:underline dark:text-blue-400"
                >
                    {t('statement.reference.order', { number: entry.orderNumber ?? entry.orderId })}
                </Link>
            )}
            {entry.memberId && (
                // A resigned member has no detail page; its reference opens the retained-data view.
                <Link
                    to={entry.memberResigned
                        ? `/members/${entry.memberId}/retained-data`
                        : `/members/${entry.memberId}`}
                    className="text-blue-600 hover:underline dark:text-blue-400"
                >
                    {t('statement.reference.member', {
                        number: entry.memberNumber ?? '',
                        name: entry.memberName ?? ''
                    })}
                </Link>
            )}
        </div>
    );
}

function PointsCell({ entry }: { entry: SocialAccountStatementEntry }) {
    const isCredit = entry.direction === "CREDIT";
    const sign = isCredit ? "+" : "−";
    const color = isCredit ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400";

    return (
        <span className={`font-medium tabular-nums ${color}`}>
            {sign}{entry.amount}
        </span>
    );
}

function BookingInfo({ entry }: { entry: SocialAccountStatementEntry }) {
    const { t } = useSocialAccountTranslation();

    return (
        <div className="space-y-2 text-xs">
            <div className="flex justify-between items-start gap-2">
                <span className="text-zinc-500 dark:text-zinc-400 shrink-0">{t('statement.info.bookedBy')}:</span>
                <span className="text-right text-zinc-900 dark:text-zinc-100">{entry.bookedByName ?? '–'}</span>
            </div>
        </div>
    );
}

export default function SocialAccountStatementPage() {
    const { token } = useAuth();
    const { t } = useSocialAccountTranslation();
    const { toast } = useToast();
    const [statement, setStatement] = useState<SocialAccountStatement | null>(null);
    const [range, setRange] = useState<StatementDateRange | null>(null);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [searchAll, setSearchAll] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [pendingReversal, setPendingReversal] = useState<SocialAccountStatementEntry | null>(null);
    const [isReversing, setIsReversing] = useState(false);

    const handleRangeChange = useCallback((period: DateRange | null) => {
        setRange(period ? { from: period.startDate, to: period.endDate } : null);
    }, []);

    // Debounce so typing does not fire one backend request per keystroke.
    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [search]);

    // Clearing the search must not leave a stale "all periods" choice armed.
    useEffect(() => {
        if (search.trim() === '') setSearchAll(false);
    }, [search]);

    const activeSearch = debouncedSearch.trim();
    const isSearching = activeSearch.length > 0;
    // searchAll drops the range so the search spans the whole account.
    const effectiveRange = isSearching && searchAll ? null : range;

    const canExport = !isLoading && !hasError && (statement?.entries.length ?? 0) > 0;

    const reversedDonationIds = useMemo(
        () => new Set((statement?.entries ?? []).map((entry) => entry.reversalOf).filter(Boolean)),
        [statement]
    );

    function isReversible(entry: SocialAccountStatementEntry): boolean {
        return entry.reason === "VOLUNTARY_DONATION"
            && entry.direction === "CREDIT"
            && entry.reversalOf === null
            && !reversedDonationIds.has(entry.id);
    }

    async function handleExport(
        download: (token: string) => Promise<{ blob: Blob; filename: string }>
    ) {
        if (!token) return;
        try {
            const { blob, filename } = await download(token);
            triggerDownload(blob, filename);
        } catch (error) {
            log.error("Error downloading social account export:", error);
            toast({ title: t('statement.export.error'), variant: 'destructive' });
        }
    }

    async function handleReverse() {
        if (!token || !pendingReversal) return;
        const entry = pendingReversal;
        setIsReversing(true);
        try {
            await reverseVoluntaryDonation(token, entry.id);
            setPendingReversal(null);
            toast({
                title: t('statement.reverse.toast.success.title'),
                description: t('statement.reverse.toast.success.description', {
                    points: entry.amount,
                    name: entry.memberName ?? ''
                })
            });
            setRefreshTrigger((n) => n + 1);
        } catch (reason) {
            const finding = (reason as ProblemDetail)?.validationIssues?.[0];
            toast({
                title: t('statement.reverse.toast.error.title'),
                description: finding ? t(finding.message, finding.args) : t('statement.reverse.toast.error.description'),
                variant: 'destructive'
            });
        } finally {
            setIsReversing(false);
        }
    }

    useEffect(() => {
        if (!token) return;
        let ignore = false;
        setIsLoading(true);
        setHasError(false);
        getSocialAccountStatement(token, effectiveRange, activeSearch || undefined)
            .then((data) => { if (!ignore) setStatement(data); })
            .catch((error) => {
                if (ignore) return;
                log.error("Error loading social account statement:", error);
                setHasError(true);
            })
            .finally(() => { if (!ignore) setIsLoading(false); });
        // Ignore stale responses so a slow earlier query cannot overwrite a newer one.
        return () => { ignore = true; };
    }, [token, effectiveRange, activeSearch, refreshTrigger]);

    return (
        <div className="container mx-auto py-6 max-w-7xl space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                    <PiggyBank className="h-8 w-8 text-blue-500" />
                    <div>
                        <Heading>{t('statement.title')}</Heading>
                        <Text className="text-sm text-zinc-500 dark:text-zinc-400">{t('statement.subtitle')}</Text>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        plain
                        onClick={() => handleExport((tok) =>
                            downloadSocialAccountStatementExcel(tok, effectiveRange, activeSearch || undefined))}
                        disabled={!canExport}
                        className="flex items-center gap-2"
                        title={t('statement.export.excel')}
                    >
                        <FileSpreadsheet className="h-4 w-4" />
                        <span className="hidden sm:inline">{t('statement.export.excel')}</span>
                    </Button>
                    <Button
                        plain
                        onClick={() => handleExport((tok) =>
                            downloadSocialAccountStatementPdf(tok, effectiveRange, activeSearch || undefined))}
                        disabled={!canExport}
                        className="flex items-center gap-2"
                        title={t('statement.export.pdf')}
                    >
                        <FileText className="h-4 w-4" />
                        <span className="hidden sm:inline">{t('statement.export.pdf')}</span>
                    </Button>
                </div>
            </div>

            <Card className="p-6 space-y-4">
                <div className="flex flex-wrap items-end gap-4">
                    <PeriodStepperFilter
                        onChange={handleRangeChange}
                        translationNamespace="socialAccount"
                        translationKeyPrefix="statement.timePeriod"
                        includeFullAccount
                        defaultType="fullAccount"
                    />
                    <Field className="flex-1 min-w-[220px]">
                        <Label>{t('statement.search.label')}</Label>
                        <InputGroup>
                            <MagnifyingGlassIcon />
                            <Input
                                type="text"
                                placeholder={t('statement.search.placeholder')}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </InputGroup>
                    </Field>
                </div>
                {isSearching && range && (
                    <Field className="flex items-center gap-2">
                        <Checkbox checked={searchAll} onChange={setSearchAll} />
                        <Label>{t('statement.search.allPeriods')}</Label>
                    </Field>
                )}
            </Card>

            {isLoading ? (
                <Card className="p-6">
                    <Text className="text-zinc-500">{t('statement.loading')}</Text>
                </Card>
            ) : hasError || !statement ? (
                <Card className="p-6">
                    <Text className="text-red-500">{t('statement.error')}</Text>
                </Card>
            ) : (
                <>
                    <Card className="p-6">
                        {!isSearching && statement.from && (
                            <div className="mb-3">
                                <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                                    {t('statement.balance.opening')}
                                </Text>
                                <div className="mt-1 text-lg font-semibold tabular-nums">
                                    {statement.openingBalance} {t('statement.balance.unit')}
                                </div>
                            </div>
                        )}
                        <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                            {isSearching
                                ? t('statement.balance.matchedSum')
                                : statement.from
                                    ? t('statement.balance.closing')
                                    : t('statement.balance.label')}
                        </Text>
                        <div className="mt-1 text-3xl font-bold tabular-nums" data-testid="social-account-balance">
                            {statement.closingBalance} {t('statement.balance.unit')}
                        </div>
                    </Card>

                    <Card className="p-6">
                        {statement.entries.length === 0 ? (
                            <Text className="text-zinc-500 dark:text-zinc-400">{t('statement.empty')}</Text>
                        ) : (
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>{t('statement.columns.date')}</TableHeader>
                                        <TableHeader>{t('statement.columns.reason')}</TableHeader>
                                        <TableHeader>{t('statement.columns.points')}</TableHeader>
                                        <TableHeader>{t('statement.columns.reference')}</TableHeader>
                                        <TableHeader>{t('statement.columns.description')}</TableHeader>
                                        <TableHeader className="text-right">{t('statement.columns.actions')}</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {statement.entries.map((entry) => (
                                        <TableRow key={entry.id} data-testid="social-account-transaction-row">
                                            <TableCell className="whitespace-nowrap text-sm">
                                                {formatDateTimeString(entry.transactionTimestamp)}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {t(`statement.reason.${entry.reason}`)}
                                            </TableCell>
                                            <TableCell>
                                                <PointsCell entry={entry} />
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                <ReferenceCell entry={entry} />
                                            </TableCell>
                                            <TableCell className="text-sm max-w-md truncate" title={entry.description}>
                                                {entry.description}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    {isReversible(entry) && (
                                                        <Button
                                                            plain
                                                            onClick={() => setPendingReversal(entry)}
                                                            aria-label={t('statement.reverse.action')}
                                                            title={t('statement.reverse.action')}
                                                            className="p-1"
                                                        >
                                                            <Trash2 className="h-4 w-4 hover:text-red-500 dark:hover:text-red-400" />
                                                        </Button>
                                                    )}
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <button
                                                                    aria-label={t('statement.info.label')}
                                                                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                                                                >
                                                                    <Info className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                                                </button>
                                                            </TooltipTrigger>
                                                            <TooltipContent
                                                                side="left"
                                                                className="w-64 p-3 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 opacity-100"
                                                            >
                                                                <BookingInfo entry={entry} />
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                </>
            )}

            <Dialog open={pendingReversal !== null} onClose={() => !isReversing && setPendingReversal(null)}>
                <DialogTitle>{t('statement.reverse.dialog.title')}</DialogTitle>
                <DialogDescription>
                    {t('statement.reverse.dialog.description', {
                        points: pendingReversal?.amount ?? 0,
                        name: pendingReversal?.memberName ?? ''
                    })}
                </DialogDescription>
                <DialogBody>
                    <Text className="text-sm text-amber-700 dark:text-amber-400">
                        {t('statement.reverse.dialog.warning')}
                    </Text>
                </DialogBody>
                <DialogActions>
                    <Button plain disabled={isReversing} onClick={() => setPendingReversal(null)}>
                        {t('statement.reverse.dialog.cancel')}
                    </Button>
                    <Button color="red" disabled={isReversing} onClick={handleReverse}>
                        {t('statement.reverse.dialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
